// Customer-facing chat for a flow that runs on the ECAP flow engine (Movorra Studio) — the R3 rides
// surface at /r3ride. Same shell/branding as the agents-driven /r3chat (AppHeader + chat-area + ChatInput),
// but the conversation goes to ECAP's public /runtime: start a session with the flow's public key, stream
// each turn over SSE, and render the hook-emitted <Quote/> / <Booking/> components as real cards.
//
// The flow key is resolved from (1) a ?key= query override (handy for testing a freshly imported flow),
// then (2) the route config (REACT_APP_R3_ECAP_FLOW_KEY at build time).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppHeader from '../layouts/AppHeader';
import ChatInput from '../components/ChatInput';
import { startSession, streamUrl } from '../services/ecapApi';
import { IconBot, IconCircleCheck, IconCar, IconCalendar, IconRoute } from '../ui/icons';
import { cx } from '../ui/cx';
import { Plus } from 'lucide-react';

function resolveKey(cfgKey) {
  try {
    const q = new URLSearchParams(window.location.search).get('key');
    if (q) return q.trim();
  } catch (e) { /* no window */ }
  return cfgKey || '';
}

function money(v) {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : (v || '—');
}

function when(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Parse a self-closing component tag (`<Quote number="Q-1" total="312.5"/>`) into { name, props }. */
function parseTag(tag) {
  const m = /^<\s*([A-Za-z][\w-]*)([^>]*)\/?>$/.exec(String(tag).trim());
  if (!m) return null;
  const props = {};
  const re = /([\w-]+)\s*=\s*"([^"]*)"/g;
  let a;
  while ((a = re.exec(m[2]))) props[a[1]] = a[2];
  return { name: m[1], props };
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-13">
      <span className="text-body/50 mt-0.5 shrink-0">{icon}</span>
      <span className="text-body/60 w-16 shrink-0">{label}</span>
      <span className="text-body font-medium">{value}</span>
    </div>
  );
}

function QuoteCard({ p }) {
  return (
    <div className="mt-2 bg-surface border border-border rounded-card p-4 max-w-[80%] shadow-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-11 font-semibold tracking-wide text-primary uppercase">Your quote</div>
        <div className="text-11 text-body/50 font-mono">{p.number}</div>
      </div>
      <div className="text-2xl font-semibold text-body mb-3">{money(p.total)}</div>
      <div className="space-y-1.5">
        <Row icon={<IconRoute size={14} />} label="Route" value={`${p.from || '—'} → ${p.to || '—'}`} />
        <Row icon={<IconCalendar size={14} />} label="Pickup" value={when(p.when)} />
        {p.returning && <Row icon={<IconCalendar size={14} />} label="Return" value={when(p.returning)} />}
        <Row icon={<IconCar size={14} />} label="Vehicle" value={p.vehicle === 'BIG_VAN' ? 'Big Van' : p.vehicle === 'SEDAN' ? 'Sedan' : (p.vehicle || '—')} />
      </div>
    </div>
  );
}

function BookingCard({ p }) {
  return (
    <div className="mt-2 bg-surface border border-border border-l-4 border-l-success rounded-card p-4 max-w-[80%] shadow-card">
      <div className="flex items-center gap-2 mb-2 text-success">
        <IconCircleCheck size={16} />
        <span className="text-11 font-semibold tracking-wide uppercase">Booking confirmed</span>
      </div>
      <div className="text-xl font-semibold text-body mb-1 font-mono">{p.number}</div>
      <div className="text-13 text-body/60 mb-3">Quote {p.quote} · {money(p.total)}{p.status ? ` · ${p.status}` : ''}</div>
      <div className="space-y-1.5">
        <Row icon={<IconRoute size={14} />} label="Route" value={`${p.from || '—'} → ${p.to || '—'}`} />
        <Row icon={<IconCalendar size={14} />} label="Pickup" value={when(p.when)} />
        <Row icon={<IconCar size={14} />} label="Vehicle" value={p.vehicle === 'BIG_VAN' ? 'Big Van' : p.vehicle === 'SEDAN' ? 'Sedan' : (p.vehicle || '—')} />
      </div>
    </div>
  );
}

// Which hook-emitted component tags render as rich cards. The default is the quote-bot set; a caller can
// pass its own map via `cfg.components` (an empty map = no rich cards, everything falls through to the
// generic renderer below). This is the seam that lets one chat page serve different KINDS of flow without
// forking the page — a quote bot gets quote/booking cards, another workflow gets the generic treatment.
const DEFAULT_COMPONENTS = { Quote: QuoteCard, Booking: BookingCard };

function ComponentCard({ tag, components }) {
  const parsed = parseTag(tag);
  if (!parsed) return null;
  const Card = (components || DEFAULT_COMPONENTS)[parsed.name];
  if (Card) return <Card p={parsed.props} />;
  return (
    <div className="mt-2 bg-white border border-border rounded-card p-3 max-w-[80%] text-13">
      {Object.entries(parsed.props).map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4"><span className="text-body/50">{k}</span><b>{v}</b></div>
      ))}
    </div>
  );
}

function Bubble({ msg, components }) {
  const me = msg.who === 'me';
  return (
    <div className={cx('flex flex-col mb-4 px-4', me ? 'items-end' : 'items-start')}>
      {!me && (
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-8 h-8 rounded-full text-white flex items-center justify-center shadow-card bg-primary"><IconBot size={16} /></div>
          <span className="text-sm font-semibold text-primary">Assistant</span>
        </div>
      )}
      {(msg.text || msg.streaming) && (
        <div className={cx('max-w-[80%] px-4 py-3 shadow-card whitespace-pre-wrap text-15 leading-relaxed',
          me ? 'bg-primary text-white rounded-card rounded-br-md' : 'bg-white border border-border rounded-card rounded-bl-md text-body')}>
          {msg.text || (msg.streaming && (
            <div className="flex items-center gap-1"><div className="dot" /><div className="dot" /><div className="dot" /></div>
          ))}
        </div>
      )}
      {(msg.components || []).map((c, i) => <ComponentCard key={i} tag={c} components={components} />)}
    </div>
  );
}

export default function EcapChatPage({ cfg, onLogout }) {
  const key = resolveKey(cfg && cfg.flowKey);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const esRef = useRef(null);
  const endRef = useRef(null);

  const start = useCallback(async () => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setError(null); setMessages([]); setSessionId(null);
    if (!key) { setError('This chat is not configured yet (no flow key).'); return; }
    setBusy(true);
    try {
      const s = await startSession(key);
      setSessionId(s.sessionId);
      if (s.greeting) setMessages([{ who: 'bot', text: s.greeting }]);
    } catch (e) {
      setError('Sorry — the chat is unavailable right now. Please try again in a moment.');
    } finally { setBusy(false); }
  }, [key]);

  useEffect(() => { start(); return () => { if (esRef.current) esRef.current.close(); }; }, [start]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages]);

  const send = (text) => {
    const t = (text || '').trim();
    if (!t || !sessionId || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { who: 'me', text: t }, { who: 'bot', text: '', components: [], streaming: true }]);
    const es = new EventSource(streamUrl(sessionId, t));
    esRef.current = es;
    // Pure updaters so React StrictMode's double-invocation can't append a chunk twice.
    const patchLast = (patch) => (m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, ...patch(msg) } : msg));
    es.addEventListener('message', (ev) => setMessages(patchLast((msg) => ({ text: msg.text + ev.data }))));
    es.addEventListener('component', (ev) => setMessages(patchLast((msg) => ({ components: [...(msg.components || []), ev.data] }))));
    es.addEventListener('done', () => { es.close(); esRef.current = null; setMessages(patchLast(() => ({ streaming: false }))); setBusy(false); });
    es.addEventListener('error', () => {
      es.close(); esRef.current = null; setBusy(false);
      setMessages((m) => (m.length ? patchLast((msg) => ({ streaming: false, text: msg.text || "Sorry — I didn't get a reply. Please try again." }))(m) : m));
    });
  };

  return (
    <div className="app">
      <AppHeader userName="" onLogout={onLogout} subtitle={(cfg && cfg.brand && cfg.brand.subtitle) || 'BOOK A RIDE'}
        logoSrc={cfg && cfg.brand && cfg.brand.logoSrc} brandName={cfg && cfg.brand && cfg.brand.brandName} variant="customer" />
      <div className="chat-area">
        {messages.length > 1 && (
          <div className="chat-clear-bar">
            <button className="chat-clear-btn" onClick={start} disabled={busy}>
              <Plus size={16} strokeWidth={2.5} /> New chat
            </button>
          </div>
        )}
        {error && (
          <div className="mx-4 mb-4 rounded-card border border-danger/30 bg-danger-50 px-4 py-3 text-13 text-body">{error}</div>
        )}
        {messages.map((msg, i) => <Bubble key={i} msg={msg} components={cfg && cfg.components} />)}
        <div ref={endRef} />
      </div>
      <ChatInput onSend={send} disabled={busy || !sessionId} />
    </div>
  );
}
